<template>
  <div
    class="flex flex-col justify-center items-center fixed z-50 inset-0 bg-opacity-25 bg-black"
  >
    <div class="form-card sm:w-2/3 relative">
      <button class="system-button ml-auto" @click.prevent="close">
        <close-icon class="w-8 fill-current" />
      </button>
      <form class="card border border-primary-500 mt-6" @submit.prevent="save">
        <!-- Template Selection Step -->
        <div v-if="!isEditing && creationMode === null" class="mb-6">
          <h2 class="text-xl font-bold mb-4">¿Cómo deseas crear la dieta?</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              @click="selectCreationMode('template')"
              class="p-6 border-2 border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
            >
              <h3 class="font-bold text-lg mb-2">📋 Usar Plantilla</h3>
              <p class="text-sm text-gray-600">
                Selecciona una plantilla predefinida y solo ingresa la ingesta diaria de la mascota.
              </p>
            </button>
            <button
              type="button"
              @click="selectCreationMode('scratch')"
              class="p-6 border-2 border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
            >
              <h3 class="font-bold text-lg mb-2">✏️ Crear desde Cero</h3>
              <p class="text-sm text-gray-600">
                Define manualmente todas las comidas y porciones
              </p>
            </button>
          </div>
        </div>

        <!-- Template Selection -->
        <div v-if="creationMode === 'template' && !selectedTemplate" class="mb-6">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-xl font-bold">Selecciona una Plantilla</h2>
            <button
              type="button"
              @click="creationMode = null"
              class="secondary-button"
            >
              ← Volver
            </button>
          </div>
          
          <div v-if="loadingTemplates" class="text-center py-8">
            <loader-icon class="w-8 h-8 animate-spin mx-auto" />
            <p class="mt-2 text-gray-600">Cargando plantillas...</p>
          </div>
          
          <div v-else-if="templates.length === 0" class="text-center py-8 text-gray-500">
            No hay plantillas disponibles
          </div>
          
          <div v-else-if="loading" class="text-center py-8">
            <loader-icon class="w-8 h-8 animate-spin mx-auto" />
            <p class="mt-2 text-gray-600">Cargando detalles de la plantilla...</p>
          </div>
          
          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              v-for="template in templates"
              :key="template.id"
              @click="selectTemplate(template)"
              class="p-4 border rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-all"
            >
              <h3 class="font-bold">{{ template.name }}</h3>
              <p v-if="template.description" class="text-sm text-gray-600 mt-1">
                {{ template.description }}
              </p>
              <div class="mt-2">
                <span
                  class="px-2 py-1 text-xs rounded"
                  :class="{
                    'bg-blue-100 text-blue-800': template.template_type === 'transition',
                    'bg-green-100 text-green-800': template.template_type === 'standard',
                    'bg-purple-100 text-purple-800': template.template_type === 'special'
                  }"
                >
                  {{ getTemplateTypeLabel(template.template_type) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Option to change to template mode when editing -->
        <div v-if="isEditing && creationMode === 'scratch' && !showFinalDetails" class="mb-4">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p class="text-blue-800 mb-2">💡 ¿Quieres usar una plantilla para simplificar la edición?</p>
            <button
              type="button"
              @click="selectCreationMode('template')"
              class="secondary-button"
            >
              📋 Cambiar a Plantilla
            </button>
          </div>
        </div>

        <!-- Daily intake input (shown for both modes after initial selection) -->
        <div v-if="isEditing || creationMode === 'scratch' || selectedTemplate" class="flex flex-col w-full mb-4">
          <label for="daily_gram_intake" class="font-bold text-lg">Ingesta diaria del perro (gramos)</label>
          <input
            id="daily_gram_intake"
            type="number"
            step="any"
            v-model="form.daily_gram_intake"
            placeholder="Ej: 200"
            class="text-lg"
            @input="onDailyIntakeChange"
          />
          <p class="text-sm text-gray-600 mt-1">
            Ingresa la cantidad total de gramos que el perro debe comer al día
          </p>
          <div v-if="form.daily_gram_intake" class="mt-2 p-3 bg-blue-50 rounded">
            <p class="font-medium">📊 Cálculo automático:</p>
            <p class="text-sm">• Ingesta diaria: {{ form.daily_gram_intake }}g</p>
            <p class="text-sm">• Total semanal: {{ (form.daily_gram_intake * 7).toFixed(2) }}g</p>
            <p class="text-sm font-bold text-blue-700">• Por comida del día: {{ form.daily_gram_intake.toFixed(2) }}g</p>
          </div>
        </div>

        <div v-if="!form.daily_gram_intake && creationMode === 'scratch'" class="p-4 bg-yellow-50 rounded mb-4">
          ⚠️ Por favor, ingresa primero la ingesta diaria del perro para continuar
        </div>

        <!-- Show template-based meal summary when using template -->
        <div v-if="selectedTemplate && form.daily_gram_intake && !showFinalDetails" class="mb-4">
          <h3 class="font-bold text-lg mb-3">📋 Vista previa de la dieta basada en plantilla</h3>
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p class="text-blue-800 font-medium">Plantilla: {{ selectedTemplate.name }}</p>
            <p class="text-blue-700 text-sm mt-1">Ingesta diaria: {{ form.daily_gram_intake }}g</p>
          </div>
          
          <div class="space-y-3">
            <div v-for="(meal, index) in form.meals" :key="index" class="border rounded-lg p-3">
              <h4 class="font-medium text-primary-500 mb-2">Comida {{ index + 1 }}</h4>
              <div v-if="meal.comments" class="mb-2 text-sm text-gray-600 italic">
                {{ meal.comments }}
              </div>
              <div class="space-y-1">
                <div v-for="portion in meal.portions" :key="portion.name" class="flex justify-between text-sm">
                  <span>{{ portion.name }} ({{ portion.category }} - {{ portion.subcategory }})</span>
                  <span class="font-medium">{{ portion.quantity.toFixed(2) }}g ({{ portion.percentage }}%)</span>
                </div>
              </div>
              <div class="mt-2 pt-2 border-t">
                <p class="text-sm font-medium text-right">Total: {{ getMealGrams(index).toFixed(2) }}g</p>
              </div>
            </div>
          </div>
          
          <div class="mt-4 p-3 bg-green-50 rounded">
            <p class="font-medium text-green-800">✅ Dieta lista para guardar</p>
            <button 
              type="button"
              class="primary-button mt-3"
              @click="showFinalDetails = true"
            >
              Continuar a detalles finales →
            </button>
          </div>
        </div>

        <!-- Original meal editing UI for scratch mode only (not for templates) -->
        <div v-if="(isEditing || creationMode === 'scratch') && !selectedTemplate && form.daily_gram_intake && !showFinalDetails">
          <!-- Tab Navigation -->
          <div class="flex flex-wrap gap-2 mb-4">
            <button
              v-for="mealIndex in 7"
              :key="mealIndex"
              @click.prevent="selectedMealTab = mealIndex - 1"
              :class="[
                'px-4 py-2 rounded-lg font-medium transition-colors relative',
                selectedMealTab === mealIndex - 1
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              ]"
            >
              <span class="flex items-center gap-2">
                <!-- Status indicator dot -->
                <span
                  :class="[
                    'w-2 h-2 rounded-full',
                    getMealStatus(mealIndex - 1) === 'excess' ? 'bg-red-500' :
                    getMealStatus(mealIndex - 1) === 'complete' ? 'bg-green-500' :
                    getMealStatus(mealIndex - 1) === 'partial' ? 'bg-yellow-500' :
                    'bg-gray-400'
                  ]"
                ></span>
                Comida {{ mealIndex }}
              </span>
            </button>
          </div>

          <!-- Progress Bar -->
          <div class="mb-4">
            <div class="flex justify-between text-sm mb-1">
              <span class="text-gray-600">Progreso: {{ completedMealsCount }}/7 comidas completas</span>
              <span :class="[
                'font-medium',
                totalGrams > (form.daily_gram_intake * 7) ? 'text-red-600' :
                Math.abs(totalGrams - (form.daily_gram_intake * 7)) < 0.01 ? 'text-green-600' :
                'text-gray-600'
              ]">
                {{ totalGrams.toFixed(2) }}g / {{ (form.daily_gram_intake * 7).toFixed(2) }}g
                <span v-if="totalGrams > (form.daily_gram_intake * 7)" class="text-red-600">
                  (+{{ (totalGrams - (form.daily_gram_intake * 7)).toFixed(2) }}g)
                </span>
              </span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2 relative">
              <div 
                :class="[
                  'h-2 rounded-full transition-all',
                  totalGrams > (form.daily_gram_intake * 7) ? 'bg-red-500' : 'bg-primary-500'
                ]"
                :style="`width: ${Math.min((completedMealsCount / 7) * 100, 100)}%`"
              ></div>
            </div>
          </div>

          <!-- Create meal button if not exists -->
          <button
            v-if="!form.meals[selectedMealTab]"
            class="secondary-button mb-4"
            @click.prevent="createMealAtIndex(selectedMealTab)"
          >
            + Crear Comida {{ selectedMealTab + 1 }}
          </button>
        </div>

        <!-- Single Meal View (only for scratch mode, not templates) -->
        <div
          v-if="!selectedTemplate && form.daily_gram_intake && form.meals[selectedMealTab] && !showFinalDetails"
          class="card border border-secondary-500 mt-4"
        >
          <div class="flex justify-between items-center mb-4">
            <p class="medium text-lg">Comida {{ selectedMealTab + 1 }}</p>
            <div class="text-sm">
              <span :class="[
                'font-medium',
                getMealGrams(selectedMealTab) > form.daily_gram_intake ? 'text-red-600' :
                getMealGrams(selectedMealTab) === form.daily_gram_intake ? 'text-green-600' : 
                'text-gray-600'
              ]">
                {{ getMealGrams(selectedMealTab).toFixed(2) }}g / {{ form.daily_gram_intake }}g
              </span>
            </div>
          </div>
          
          <!-- Warning message if exceeding daily intake -->
          <div v-if="getMealGrams(selectedMealTab) > form.daily_gram_intake" 
               class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div class="flex items-center gap-2">
              <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
              <div>
                <p class="font-medium text-red-800">¡Exceso de porciones!</p>
                <p class="text-sm text-red-700">
                  Has agregado <span class="font-bold">{{ (getMealGrams(selectedMealTab) - form.daily_gram_intake).toFixed(2) }}g</span> más de la ingesta diaria.
                </p>
              </div>
            </div>
          </div>

          <!-- Copy from previous meal button -->
          <button
            v-if="selectedMealTab > 0 && form.meals[selectedMealTab - 1] && form.meals[selectedMealTab - 1].portions.length > 0 && form.meals[selectedMealTab].portions.length === 0"
            class="tertiary-button mb-4"
            @click.prevent="copyFromPreviousMeal(selectedMealTab)"
          >
            📋 Copiar de Comida {{ selectedMealTab }}
          </button>

          <div
            v-for="(portion, j) in form.meals[selectedMealTab].portions"
            :key="j"
            class="card border border-tertiary-500 mt-4"
          >
            <div 
              class="flex justify-between items-center cursor-pointer"
              @click="portion.saved ? togglePortionCollapse(j) : null"
            >
              <div class="flex items-center gap-3">
                <p class="medium">Porción #{{ j + 1 }}</p>
                <span v-if="portion.saved && portion.collapsed" class="text-sm text-gray-600">
                  {{ getSubCategoryName(portion.diet_subcategory_id) }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <span v-if="portion.saved" class="text-sm text-green-600 font-medium">
                  {{ portion.quantity }}g
                </span>
                <svg 
                  v-if="portion.saved"
                  :class="['w-5 h-5 transition-transform', portion.collapsed ? '' : 'rotate-180']"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>

            <div v-if="!form.meals[selectedMealTab].portions[j].saved">
              <!-- Category and Subcategory in two columns -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex flex-col w-full">
                  <label for="category">Categoría</label>
                  <custom-select
                    id="category"
                    v-model="form.meals[selectedMealTab].portions[j].diet_category_id"
                    :options="getCategoryOptions()"
                    @change="form.meals[selectedMealTab].portions[j].diet_subcategory_id = null"
                  />
                </div>

                <div class="flex flex-col w-full">
                  <label for="subcategory">Subcategoría</label>
                  <custom-select
                    id="subcategory"
                    v-model="form.meals[selectedMealTab].portions[j].diet_subcategory_id"
                    :disabled="
                      form.meals[selectedMealTab].portions[j].diet_category_id === null
                    "
                    :options="
                      getSubcategoryOptions(
                        getSubCategories(
                          form.meals[selectedMealTab].portions[j].diet_category_id
                        )
                      )
                    "
                  />
                </div>
              </div>

              <div class="flex flex-col w-full mt-4">
                <label for="quantity">
                  Cantidad (gramos)
                  <span v-if="getMealGrams(selectedMealTab) > 0" 
                        :class="[
                          'text-sm ml-2',
                          getRemainingGrams(selectedMealTab) < 0 ? 'text-red-600' :
                          getRemainingGrams(selectedMealTab) === 0 ? 'text-green-600' :
                          'text-gray-500'
                        ]">
                    ({{ getRemainingGrams(selectedMealTab) > 0 ? 'Faltan' : getRemainingGrams(selectedMealTab) < 0 ? 'Exceso de' : 'Completo' }} 
                    {{ Math.abs(getRemainingGrams(selectedMealTab)).toFixed(2) }}g)
                  </span>
                </label>
                <input
                  id="quantity"
                  type="number"
                  step="any"
                  v-model="form.meals[selectedMealTab].portions[j].quantity"
                  :placeholder="`Parte de los ${form.daily_gram_intake}g diarios`"
                  :class="[
                    getRemainingGrams(selectedMealTab) < 0 && form.meals[selectedMealTab].portions[j].quantity ? 
                    'border-red-300 bg-red-50' : ''
                  ]"
                />
                <validation-errors
                  :errors="errorFor('quantity')"
                ></validation-errors>
              </div>

              <button
                class="secondary-button mt-4"
                @click.prevent="savePortion(selectedMealTab, j)"
              >
                Guardar Porción
              </button>

              <button
                class="delete-button mt-2 block"
                @click.prevent="form.meals[selectedMealTab].portions.splice(j, 1)"
              >
                Eliminar
              </button>
            </div>

            <div v-else-if="!portion.collapsed">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                <div class="text-sm">
                  <p class="font-medium text-gray-600">Categoría</p>
                  <p class="font-medium">
                    {{
                      getCategoryName(form.meals[selectedMealTab].portions[j].diet_category_id)
                    }}
                  </p>
                </div>

                <div class="text-sm">
                  <p class="font-medium text-gray-600">Subcategoría</p>
                  <p class="font-medium">
                    {{
                      getSubCategoryName(
                        form.meals[selectedMealTab].portions[j].diet_subcategory_id
                      )
                    }}
                  </p>
                </div>

                <div class="text-sm">
                  <p class="font-medium text-gray-600">Cantidad</p>
                  <p class="font-medium text-green-600">{{ form.meals[selectedMealTab].portions[j].quantity }}g</p>
                </div>
              </div>

              <button
                class="cancel-button mt-2 block"
                @click.prevent="form.meals[selectedMealTab].portions[j].saved = false"
              >
                Editar
              </button>

              <button
                class="delete-button mt-2 block"
                @click.prevent="form.meals[selectedMealTab].portions.splice(j, 1)"
              >
                Eliminar
              </button>
            </div>
          </div>

          <!-- Move Create Portion button to bottom -->
          <button 
            class="secondary-button mt-4" 
            @click.prevent="createPortion(selectedMealTab)"
          >
            + Agregar Porción
          </button>

          <div class="flex flex-col w-full mt-4">
            <label for="comments">Comentarios</label>
            <textarea
              class="h-28"
              id="comments"
              type="text"
              v-model="form.meals[selectedMealTab].comments"
            />
          </div>

          <button
            v-if="canDuplicate(selectedMealTab)"
            class="cancel-button mt-2 block"
            @click.prevent="duplicateMeal(selectedMealTab)"
          >
            Duplicar a siguiente comida
          </button>

          <button
            class="delete-button mt-2 block"
            @click.prevent="deleteMealAtIndex(selectedMealTab)"
          >
            Eliminar Comida
          </button>
        </div>

        <!-- Show button to go to final details if meals completed but not showing final details yet -->
        <div v-if="creationMode === 'scratch' && mealsCompleted && !showFinalDetails" class="mt-6">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p class="text-blue-800 font-medium mb-3">✨ ¡Excelente! Has completado todas las comidas</p>
            <button 
              class="primary-button"
              @click.prevent="showFinalDetails = true"
            >
              Continuar a detalles de la dieta →
            </button>
          </div>
        </div>

        <!-- Final Diet Details Page -->
        <div v-if="showFinalDetails" @submit.prevent="createDiet">
          <!-- Success header -->
          <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <h3 class="text-lg font-bold text-green-800 mb-2">✅ ¡Todas las comidas están completas!</h3>
            <p class="text-green-700">Ahora configura los detalles finales de la dieta</p>
            <p v-if="selectedTemplate" class="text-sm text-green-600 mt-2">
              📋 Usando plantilla: {{ selectedTemplate.name }}
            </p>
          </div>

          <!-- Summary of meals -->
          <div class="mb-6">
            <h4 class="font-bold mb-3">Resumen de comidas:</h4>
            <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              <div 
                v-for="(meal, index) in form.meals"
                :key="index"
                :class="[
                  'text-center p-2 bg-gray-50 rounded',
                  creationMode === 'scratch' ? 'cursor-pointer hover:bg-gray-100' : ''
                ]"
                @click="creationMode === 'scratch' ? (showFinalDetails = false, selectedMealTab = index) : null"
              >
                <p class="text-xs font-medium">Comida {{ index + 1 }}</p>
                <p class="text-sm font-bold text-green-600">{{ getMealGrams(index).toFixed(0) }}g</p>
                <p class="text-xs text-gray-500">{{ meal.portions.length }} porciones</p>
              </div>
            </div>
            <div class="mt-3 p-3 bg-blue-50 rounded">
              <p class="text-sm"><span class="font-medium">Total semanal:</span> {{ totalGrams.toFixed(2) }}g</p>
              <p class="text-sm"><span class="font-medium">Ingesta diaria:</span> {{ form.daily_gram_intake }}g</p>
            </div>
          </div>

          <div class="flex flex-col w-full mt-4">
            <label for="name">Nombre de dieta</label>
            <input
              id="name"
              type="text"
              v-model="form.name"
            />
            <validation-errors
              :errors="errorFor('name')"
            ></validation-errors>
          </div>

          <div class="flex flex-col w-full mt-4">
            <label for="weekly_price">Precio por semana</label>
            <input
              id="weekly_price"
              type="number"
              step="any"
              v-model="form.weekly_price"
            />
            <validation-errors
              :errors="errorFor('weekly_price')"
            ></validation-errors>
          </div>

          <div class="flex flex-col w-full mt-4">
            <label for="daily_gram_intake">Ingesta diaria (gramos)</label>
            <input
              id="daily_gram_intake"
              type="number"
              step="any"
              v-model="form.daily_gram_intake"
              placeholder="Ej: 200"
            />
            <validation-errors
              :errors="errorFor('daily_gram_intake')"
            ></validation-errors>
            <p class="text-sm text-gray-600 mt-1">
              Total semanal: {{ form.daily_gram_intake ? (form.daily_gram_intake * 7).toFixed(0) : 0 }}g
            </p>
          </div>

          <!-- Daily intake validation summary -->
          <div v-if="gramTotalMismatch" class="p-3 bg-red-50 rounded mt-4">
            <p class="text-sm text-error-500 font-medium">
              ⚠️ Error de validación:
            </p>
            <p class="text-sm text-error-500">
              • Suma actual de porciones: {{ totalGrams.toFixed(2) }}g
            </p>
            <p class="text-sm text-error-500">
              • Total esperado ({{ form.daily_gram_intake }}g × 7 días): {{ (form.daily_gram_intake * 7).toFixed(2) }}g
            </p>
            <p class="text-sm text-error-500">
              • Diferencia: {{ Math.abs(totalGrams - (form.daily_gram_intake * 7)).toFixed(2) }}g
            </p>
          </div>

          <div class="flex flex-col w-full mt-4">
            <label for="diet_stage">Etapa</label>
            <custom-select
              id="diet_stage"
              v-model="form.diet_stage_id"
              :options="getDietStageOptions()"
            />
            <!-- <select id="diet_stage" v-model="form.diet_stage_id">
          <option :value="null" selected>Seleccionar</option>
          <option
            v-for="dietStage in dietStages"
            :key="dietStage.id"
            :value="dietStage.id"
          >
            {{ dietStage.display_name }}
          </option>
        </select> -->
            <validation-errors
              :errors="errorFor('diet_stage_id')"
            ></validation-errors>
          </div>

          <div class="flex flex-col w-full mt-4">
            <label for="diet_type">Tipo</label>
            <custom-select
              id="diet_type"
              v-model="form.diet_type_id"
              :options="getDietTypeOptions()"
            />
            <!-- <select id="diet_type" v-model="form.diet_type_id">
          <option :value="null" selected>Seleccionar</option>
          <option
            v-for="dietType in dietTypes"
            :key="dietType.id"
            :value="dietType.id"
          >
            {{ dietType.display_name }}
          </option>
        </select> -->
            <validation-errors
              :errors="errorFor('diet_type_id')"
            ></validation-errors>
          </div>

          <div class="flex flex-col w-full mt-4">
            <label for="description">Descripción para el cliente</label>
            <textarea
              class="h-28"
              id="description"
              type="text"
              v-model="form.description"
              placeholder="Esta descripción será visible para el cliente"
            />
          </div>

          <div class="flex flex-col w-full mt-4">
            <label for="comments">Comentarios internos</label>
            <textarea
              class="h-28"
              id="comments"
              type="text"
              v-model="form.comments"
            />
          </div>

          <div class="flex gap-2 mt-4">
            <button
              type="button"
              class="cancel-button"
              @click.prevent="showFinalDetails = false"
            >
              ← Volver a las comidas
            </button>
            <primary-button class="mx-0" :loading="loading" type="submit"
              >{{ isEditing ? "Guardar" : "Crear" }} Dieta</primary-button
            >
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import validationErrors from "@/mixins/validationErrors";
import { is422 } from "@/utils/response";
import LoaderIcon from "@/icons/LoaderIcon";

export default {
  name: "Diet Form",
  
  components: {
    LoaderIcon
  },

  props: {
    petId: Number,
    dietCategories: Array,
    dietStages: Array,
    dietTypes: Array,
    diet: Object,
  },

  data() {
    return {
      loading: false,
      selectedMealTab: 0,
      showFinalDetails: false,
      creationMode: null, // 'template' or 'scratch'
      selectedTemplate: null,
      templates: [],
      loadingTemplates: false,
      form: {
        meals: [],
        name: null,
        weekly_price: null,
        daily_gram_intake: null,
        diet_stage_id: null,
        diet_type_id: null,
        description: null,
        comments: null,
        pet_id: null,
      },
      isEditing: false,
    };
  },

  computed: {
    mealsCompleted() {
      // For template mode, meals are complete when template is applied
      if (this.selectedTemplate && this.form.meals.length === 7) {
        return true;
      }
      
      // For scratch mode, check each meal
      if (this.form.meals.length !== 7) {
        return false;
      }

      for (let meal of this.form.meals) {
        if (meal.portions.length === 0) {
          return false;
        }

        for (let portion of meal.portions) {
          if (!portion.saved) {
            return false;
          }
        }
      }

      return true;
    },
    
    totalGrams() {
      let total = 0;
      for (let meal of this.form.meals) {
        for (let portion of meal.portions) {
          if (portion.quantity) {
            total += parseFloat(portion.quantity);
          }
        }
      }
      return total;
    },
    
    gramTotalMismatch() {
      if (!this.form.daily_gram_intake || !this.mealsCompleted) {
        return false;
      }
      const expectedTotal = this.form.daily_gram_intake * 7;
      return Math.abs(this.totalGrams - expectedTotal) > 0.01;
    },
    
    completedMealsCount() {
      let count = 0;
      for (let i = 0; i < this.form.meals.length; i++) {
        if (this.getMealStatus(i) === 'complete') {
          count++;
        }
      }
      return count;
    },
  },

  mixins: [validationErrors],

  created() {
    if (this.diet) {
      this.isEditing = true;
      this.form = JSON.parse(JSON.stringify(this.diet));

      // Set to scratch mode by default for ALL existing diets when editing
      this.creationMode = 'scratch';

      // Calculate daily_gram_intake from existing meals if not present
      if (!this.form.daily_gram_intake && this.form.total_quantity) {
        // Total quantity is weekly, so divide by 7 for daily intake
        this.form.daily_gram_intake = Math.round(this.form.total_quantity / 7);
      }

      // Show final details if all meals are complete
      if (this.mealsCompleted) {
        this.showFinalDetails = true;
      }
    }
  },

  methods: {
    close() {
      this.$emit("close");
    },

    async selectCreationMode(mode) {
      this.creationMode = mode;
      if (mode === 'template') {
        await this.loadTemplates();
      }
    },

    async loadTemplates() {
      this.loadingTemplates = true;
      try {
        const params = [
          { name: 'is_active', value: '1' }
        ];
        const response = await api.dietTemplates.getAllWithQueryParams(params);
        this.templates = response.data || [];
      } catch (error) {
        console.error('Error loading templates:', error);
        this.$store.commit("setSnack", {
          message: "Error al cargar las plantillas",
          color: "error"
        });
      } finally {
        this.loadingTemplates = false;
      }
    },

    async selectTemplate(template) {
      this.loading = true;
      try {
        // Load full template with meals and portions
        const fullTemplate = await api.dietTemplates.getById(template.id);
        this.selectedTemplate = fullTemplate;

        // If we already have a daily intake value (when editing), apply the template immediately
        if (this.form.daily_gram_intake) {
          this.applyTemplateToMeals();
        }
      } catch (error) {
        console.error('Error loading template details:', error);
        this.$store.commit("setSnack", {
          message: "Error al cargar los detalles de la plantilla",
          color: "error"
        });
      } finally {
        this.loading = false;
      }
    },

    getTemplateTypeLabel(type) {
      const labels = {
        transition: 'Transición',
        standard: 'Estándar',
        special: 'Especial'
      };
      return labels[type] || type;
    },

    applyTemplateToMeals() {
      if (!this.selectedTemplate || !this.form.daily_gram_intake) return;

      console.log('Applying template with daily intake:', this.form.daily_gram_intake);

      // Clear existing meals
      this.form.meals = [];

      // Create meals from template
      // Each meal represents one day of the week, and should contain the full daily intake
      this.selectedTemplate.meals.forEach(templateMeal => {
        const meal = {
          meal_number: templateMeal.meal_number,
          comments: templateMeal.comments || '',
          portions: []
        };

        // Calculate quantities based on percentages and daily intake
        // Each meal gets the full daily intake, divided by percentages
        templateMeal.portions.forEach(templatePortion => {
          const dailyGrams = parseFloat(this.form.daily_gram_intake);
          const percentageDecimal = parseFloat(templatePortion.percentage) / 100;
          // Each meal should have portions that sum to the daily intake
          const quantity = dailyGrams * percentageDecimal;

          console.log(`Portion ${templatePortion.name}: ${templatePortion.percentage}% of ${dailyGrams}g = ${quantity}g`);
          
          // Find category and subcategory IDs from names
          let dietCategoryId = null;
          let dietSubcategoryId = null;
          
          // Find the category by name
          const category = this.dietCategories.find(cat => 
            cat.name === templatePortion.category
          );
          
          if (category) {
            dietCategoryId = category.id;
            
            // Find the subcategory within this category
            const subcategory = category.diet_subcategories.find(sub => 
              sub.name === templatePortion.subcategory
            );
            
            if (subcategory) {
              dietSubcategoryId = subcategory.id;
            }
          }
          
          meal.portions.push({
            name: templatePortion.name,
            quantity: quantity,
            percentage: templatePortion.percentage,
            category: templatePortion.category,
            subcategory: templatePortion.subcategory,
            diet_category_id: dietCategoryId,
            diet_subcategory_id: dietSubcategoryId,
            saved: true // Mark as saved since it's from template
          });
        });
        
        this.form.meals.push(meal);
      });
    },

    getCategoryOptions() {
      const options = [{ value: null, label: "Seleccionar", disabled: false }];

      this.dietCategories.forEach((categories) => {
        options.push({
          value: categories.id,
          label: categories.name,
        });
      });

      return options;
    },

    getSubcategoryOptions(subcategories) {
      const options = [{ value: null, label: "Seleccionar", disabled: false }];

      subcategories.forEach((category) => {
        options.push({
          value: category.id,
          label: category.name,
        });
      });

      return options;
    },

    getDietStageOptions() {
      const options = [{ value: null, label: "Seleccionar", disabled: false }];

      this.dietStages.forEach((stage) => {
        options.push({
          value: stage.id,
          label: stage.display_name,
        });
      });

      return options;
    },

    getDietTypeOptions() {
      const options = [{ value: null, label: "Seleccionar", disabled: false }];

      this.dietTypes.forEach((type) => {
        options.push({
          value: type.id,
          label: type.display_name,
        });
      });

      return options;
    },

    createMeal() {
      this.form.meals.push({
        portions: [],
        comments: "",
      });
    },

    createPortion(index) {
      this.form.meals[index].portions.push({
        quantity: null,
        diet_category_id: null,
        diet_subcategory_id: null,
      });
    },

    savePortion(categoryIndex, subcategoryIndex) {
      const portion = this.form.meals[categoryIndex].portions[subcategoryIndex];

      if (!portion.quantity || portion.diet_subcategory_id === null) {
        this.$store.dispatch(
          "errorSnack",
          "Es necesario llenar todos los campos"
        );
        return;
      }

      this.form.meals[categoryIndex].portions[subcategoryIndex].saved = true;
      this.form.meals[categoryIndex].portions[subcategoryIndex].collapsed = true;
    },
    
    togglePortionCollapse(portionIndex) {
      const portion = this.form.meals[this.selectedMealTab].portions[portionIndex];
      if (portion.collapsed === undefined) {
        this.$set(portion, 'collapsed', false);
      } else {
        portion.collapsed = !portion.collapsed;
      }
    },

    canDuplicate(categoryIndex) {
      if (
        this.form.meals[categoryIndex].portions.length === 0 ||
        this.form.meals.length >= 7
      ) {
        return false;
      }

      for (let portion of this.form.meals[categoryIndex].portions) {
        if (!portion.saved) {
          return false;
        }
      }

      return true;
    },

    duplicateMeal(categoryIndex) {
      const meal = JSON.parse(JSON.stringify(this.form.meals[categoryIndex]));

      this.form.meals.push(meal);
    },

    getSubCategories(categoryId) {
      if (categoryId === null) {
        return [];
      }

      var subcategories = [];

      this.dietCategories.forEach((category) => {
        if (category.id === categoryId) {
          subcategories = category.diet_subcategories;
        }
      });

      return subcategories;
    },

    getCategoryName(id) {
      const category = this.dietCategories.find(category => category.id === id);
      return category ? category.name : '';
    },

    getSubCategoryName(id) {
      for (let category of this.dietCategories) {
        for (let subcategory of category.diet_subcategories) {
          if (subcategory.id === id) {
            return subcategory.name;
          }
        }
      }
      return '';
    },
    
    onDailyIntakeChange() {
      if (this.selectedTemplate) {
        // Apply template with new daily intake
        this.applyTemplateToMeals();
      } else if (this.creationMode === 'scratch') {
        // Original calculation for scratch mode
        this.calculateMealPortions();
      }
    },

    calculateMealPortions() {
      // This method is called when daily_gram_intake changes
      // It helps users understand how much each meal should have
      if (this.form.daily_gram_intake) {
        // Each of the 7 meals should have the daily intake amount
        // Since we feed once per day, each meal = daily intake
        const gramsPerMeal = this.form.daily_gram_intake;
        console.log(`Cada comida debe tener ${gramsPerMeal}g`);
      }
    },
    
    getMealStatus(mealIndex) {
      if (!this.form.meals[mealIndex]) {
        return 'empty';
      }
      
      const meal = this.form.meals[mealIndex];
      if (meal.portions.length === 0) {
        return 'empty';
      }
      
      const mealGrams = this.getMealGrams(mealIndex);
      
      // Check if exceeding daily intake
      if (mealGrams > this.form.daily_gram_intake) {
        return 'excess';
      }
      
      // Check if exactly matching daily intake
      if (Math.abs(mealGrams - this.form.daily_gram_intake) < 0.01) {
        return 'complete';
      }
      
      return 'partial';
    },
    
    getMealGrams(mealIndex) {
      if (!this.form.meals[mealIndex]) {
        return 0;
      }
      
      let total = 0;
      for (let portion of this.form.meals[mealIndex].portions) {
        if (portion.quantity) {
          total += parseFloat(portion.quantity);
        }
      }
      return total;
    },
    
    getRemainingGrams(mealIndex) {
      const mealGrams = this.getMealGrams(mealIndex);
      return this.form.daily_gram_intake - mealGrams;
    },
    
    createMealAtIndex(index) {
      // Fill empty slots if needed
      while (this.form.meals.length <= index) {
        this.form.meals.push({
          portions: [],
          comments: "",
        });
      }
    },
    
    deleteMealAtIndex(index) {
      this.form.meals.splice(index, 1);
      // If we deleted the current tab, go to previous tab
      if (this.selectedMealTab >= this.form.meals.length && this.selectedMealTab > 0) {
        this.selectedMealTab--;
      }
    },
    
    copyFromPreviousMeal(targetIndex) {
      if (targetIndex > 0 && this.form.meals[targetIndex - 1]) {
        const previousMeal = this.form.meals[targetIndex - 1];
        const copiedPortions = JSON.parse(JSON.stringify(previousMeal.portions));
        this.form.meals[targetIndex].portions = copiedPortions;
        this.$store.dispatch("successSnack", "Porciones copiadas de la comida anterior");
      }
    },

    save() {
      if (!this.validateForm()) {
        this.$store.dispatch(
          "errorSnack",
          "Favor de llenar todos los campos correctamente."
        );
        return;
      }

      if (!this.isEditing) {
        this.createDiet();
      } else {
        this.updateDiet();
      }
    },

    validateForm() {
      this.errors = {};

      if (!this.form.name) {
        this.errors["name"] = ["Campo requerido"];
      }

      if (!this.form.weekly_price) {
        this.errors["weekly_price"] = ["Campo requerido"];
      }
      
      if (!this.form.daily_gram_intake) {
        this.errors["daily_gram_intake"] = ["Campo requerido"];
      }
      
      // Validate that gram total matches daily intake * 7
      if (this.form.daily_gram_intake && this.gramTotalMismatch) {
        this.errors["daily_gram_intake"] = [
          `La suma de porciones (${this.totalGrams.toFixed(2)}g) debe ser igual a la ingesta diaria × 7 (${(this.form.daily_gram_intake * 7).toFixed(2)}g)`
        ];
      }

      if (!this.form.diet_stage_id) {
        this.errors["diet_stage_id"] = ["Campo requerido"];
      }

      if (!this.form.diet_type_id) {
        this.errors["diet_type_id"] = ["Campo requerido"];
      }

      return Object.keys(this.errors).length === 0;
    },

    async createDiet() {
      this.loading = true;

      this.form.pet_id = this.petId;

      try {
        const response = await api.diets.create(this.form);
        this.$store.dispatch("successSnack", response.message);
        this.$emit("dietCreated");
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async updateDiet() {
      this.loading = true;

      try {
        const response = await api.diets.update(this.diet.id, this.form);
        this.$store.dispatch("successSnack", response.message);
        this.$emit("dietUpdated");
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },
  },
};
</script>